import { UseMutationOptions } from "@tanstack/react-query";
import {
  CapitalCallDeleteArgs,
  DistributionDeleteArgs,
  FundFamilySubEventDeleteArgs,
  WaterfallRunDeleteArgs,
} from "features/capital-activity/event/CapitalEventTypes";
import { WATERFALL_RUN_MUTATION_KEY } from "features/capital-activity/event/useWaterfallFundFamilySubEventIds";
import { useCapitalCallDelete } from "gen/endpoints/capital-call/capital-call";
import { useDistributionDelete } from "gen/endpoints/distributions/distributions";
import { useEqualizationCreate } from "gen/endpoints/equalization/equalization";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import {
  useFundFamilySubEventDelete,
  useWaterfallCreateAndRun,
  useWaterfallRunDelete,
  useWaterfallRunUpsert,
} from "gen/endpoints/fund-family-sub-event/fund-family-sub-event";
import { CapitalActivityCellTypeEnum } from "gen/model/capitalActivityCellTypeEnum";
import { CapitalActivityMetadata } from "gen/model/capitalActivityMetadata";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { GenericTableColumn } from "gen/model/genericTableColumn";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { DataTableOnButtonClickFn } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { rootRoute } from "shared/navigation/Routes";

export const useCapitalEventButtonClick = (
  fundFamilyId: string,
  fundFamilyEventId: string,
  openEqualizationModal: () => void,
  invalidateQueries: () => void,
) => {
  const { t } = useTranslation(["common", "capitalActivity", "capitalCall"]);
  const navigate = useNavigate();
  const { showErrorToast } = useToast();

  const activityEventRoute = useMemo(
    () =>
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId }),
    [fundFamilyEventId, fundFamilyId],
  );

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const mutationOptions = {
    mutation: {
      onSettled: invalidateQueries,
    },
  } satisfies {
    mutation: UseMutationOptions;
  };

  const { mutateAsync: createAndRunWaterfall } = useWaterfallCreateAndRun({
    mutation: {
      ...mutationOptions.mutation,
      mutationKey: WATERFALL_RUN_MUTATION_KEY,
    },
  });

  const { mutateAsync: createWaterfall } = useWaterfallRunUpsert({
    mutation: {
      ...mutationOptions.mutation,
      mutationKey: WATERFALL_RUN_MUTATION_KEY,
      onSuccess: (_, { fundFamilySubEventId, fundEntityId }) => {
        navigate(
          activityEventRoute
            .waterfall({})
            .hybrid({ fundFamilySubEventId, fundEntityId })
            .status({}).$,
        );
      },
    },
  });

  const { mutateAsync: deleteSubEvent } =
    useFundFamilySubEventDelete(mutationOptions);
  const { mutateAsync: deleteCapitalCall } =
    useCapitalCallDelete(mutationOptions);
  const { mutateAsync: deleteDistribution } =
    useDistributionDelete(mutationOptions);
  const { mutateAsync: deleteWaterfallRun } =
    useWaterfallRunDelete(mutationOptions);
  const { mutateAsync: createEqualization } =
    useEqualizationCreate(mutationOptions);

  const confirmCapitalCallDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<CapitalCallDeleteArgs>,
    {
      title: t("capitalActivity:capitalCallDeleteDialog.title"),
      description: t("capitalActivity:capitalCallDeleteDialog.description"),
      successMessage: t("capitalActivity:capitalCallDeleteDialog.success"),
      performDelete: deleteCapitalCall,
    },
  );

  const confirmDistributionDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<DistributionDeleteArgs>,
    {
      title: t("capitalActivity:distributionDeleteDialog.title"),
      description: t("capitalActivity:distributionDeleteDialog.description"),
      successMessage: t("capitalActivity:distributionDeleteDialog.success"),
      performDelete: deleteDistribution,
    },
  );

  const confirmEqualizationDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<FundFamilySubEventDeleteArgs>,
    {
      title: t("capitalActivity:equalizationDeleteDialog.title"),
      description: t("capitalActivity:equalizationDeleteDialog.description"),
      successMessage: t("capitalActivity:equalizationDeleteDialog.success"),
      performDelete: deleteSubEvent,
    },
  );

  const confirmWaterfallDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<WaterfallRunDeleteArgs>,
    {
      title: t("capitalActivity:waterfallDeleteDialog.title"),
      description: t("capitalActivity:waterfallDeleteDialog.description"),
      successMessage: t("capitalActivity:waterfallDeleteDialog.success"),
      performDelete: deleteWaterfallRun,
    },
  );

  const onClickColumnEdit = useCallback(
    (column: GenericTableColumn) => {
      if (column.id === "capital_call") {
        navigate(activityEventRoute.calls({}).$);
      } else if (column.id === "distribution") {
        const distributionRoute = activityEventRoute
          .distributions({})
          .capitalActivityEventDistributions({}).$;
        navigate(distributionRoute);
      }
    },
    [activityEventRoute, navigate],
  );

  const onClickCellEdit = useCallback(
    (column: GenericTableColumn, metadata: CapitalActivityMetadata) => {
      if (column.id === "equalization") {
        navigate(
          activityEventRoute.equalization({}).view({
            fundFamilySubEventId: metadata.fund_family_sub_event_id as string,
          }).$,
        );
      }
    },
    [activityEventRoute, navigate],
  );

  const onClickColumnDelete = useCallback(
    async (column: GenericTableColumn) => {
      switch (column.id) {
        case CapitalActivityCellTypeEnum.capital_call:
          await confirmCapitalCallDelete({
            fundFamilyId,
            fundFamilyEventId,
          });
          break;
        case CapitalActivityCellTypeEnum.distribution:
          await confirmDistributionDelete({
            fundFamilyId,
            fundFamilyEventId,
          });
          break;
      }
    },
    [
      confirmCapitalCallDelete,
      confirmDistributionDelete,
      fundFamilyEventId,
      fundFamilyId,
    ],
  );

  const onClickCellDelete = useCallback(
    async (column: GenericTableColumn, metadata: CapitalActivityMetadata) => {
      switch (column.id) {
        case CapitalActivityCellTypeEnum.equalization:
          await confirmEqualizationDelete({
            fundFamilyId,
            fundFamilyEventId,
            fundFamilySubEventId: metadata.fund_family_sub_event_id as string,
          });
          break;
        case CapitalActivityCellTypeEnum.waterfall:
          await confirmWaterfallDelete({
            fundFamilyId,
            fundFamilySubEventId: metadata.fund_family_sub_event_id as string,
            fundEntityId: metadata.fund_id as string,
          });
          break;
      }
    },
    [
      confirmEqualizationDelete,
      confirmWaterfallDelete,
      fundFamilyEventId,
      fundFamilyId,
    ],
  );

  return useCallback<DataTableOnButtonClickFn>(
    async ({ columnContext, cell, buttonType, event }) => {
      try {
        const column = columnContext.column;

        if (!cell) {
          if (buttonType === "edit") {
            onClickColumnEdit(column);
          } else if (buttonType === "delete") {
            await onClickColumnDelete(column);
          }
          return;
        }

        const metadata = cell.metadata;
        if (metadata?.type !== "capital_activity") {
          return;
        }
        if (buttonType === "edit") {
          onClickCellEdit(column, metadata);
          return;
        }
        if (buttonType === "delete") {
          await onClickCellDelete(column, metadata);
          return;
        }
        const distributionRoute = activityEventRoute
          .distributions({})
          .capitalActivityEventDistributions({}).$;
        switch (metadata.cell_type) {
          case CapitalActivityCellTypeEnum.capital_call:
            navigate(activityEventRoute.calls({}).$);
            return;
          case CapitalActivityCellTypeEnum.distribution:
            navigate(distributionRoute);
            return;
          case CapitalActivityCellTypeEnum.equalization:
            if (
              !metadata.fund_family_sub_event_id ||
              metadata.celery_state ===
                CeleryWorkflowStateMachineFieldsEnum.failed
            ) {
              if (fundFamilyConfig.allow_equalization_interest_opt_out) {
                event.preventDefault();
                openEqualizationModal();
              } else {
                await createEqualization({
                  fundFamilyId,
                  fundFamilyEventId,
                  data: {
                    opt_out_of_equalization_interest: false,
                  },
                });
              }
            }
            return;
          case CapitalActivityCellTypeEnum.waterfall:
            if (!metadata.waterfall_run_id || metadata.out_of_date) {
              event.preventDefault();
              if (metadata.waterfall_is_hybrid) {
                await createWaterfall({
                  fundFamilyId,
                  fundFamilySubEventId:
                    metadata.fund_family_sub_event_id as string,
                  fundEntityId: metadata.fund_id as string,
                  data: {},
                });
              } else {
                await createAndRunWaterfall({
                  fundFamilyId,
                  fundFamilySubEventId:
                    metadata.fund_family_sub_event_id as string,
                  fundEntityId: metadata.fund_id as string,
                  data: {},
                });
              }
            }
            return;
        }
      } catch (e) {
        showErrorToast(e);
      }
    },
    [
      activityEventRoute,
      onClickColumnEdit,
      onClickColumnDelete,
      onClickCellEdit,
      onClickCellDelete,
      navigate,
      fundFamilyConfig.allow_equalization_interest_opt_out,
      openEqualizationModal,
      createEqualization,
      fundFamilyId,
      fundFamilyEventId,
      createWaterfall,
      createAndRunWaterfall,
      showErrorToast,
    ],
  );
};
